import {
  extractLoginQuestionnaireFields,
  extractLoginQuestionnaireFieldsFromEnvelope,
  normalizeQuestionnaireCompletionPercentage,
  persistLoginQuestionnaireFields,
} from './auth';
import api from './api';
import { ENDPOINTS } from './endpoints';
import {
  buildSectionsFromApi,
  flatStepsFromSections,
  mergeOptionsIntoQuestionnairePayload,
  mergeValidationIntoQuestionnairePayload,
} from '../utils/medicalQuestionnaireSections';
import {
  buildYesNoWithTextareaSaveRow,
  mapPrefillValuesToOptionIds,
  rawOptionsHaveAuthoritativeIds,
  resolveOptionIdsForSubmit,
  stepDetailInputVisible,
} from '../utils/medicalQuestionnaireChoiceOptions';
import { buildSummaryUiSectionsFromSummaryEnvelope } from '../utils/medicalQuestionnaireSummaryDisplay';

/** Flatten nested answer objects from DRF / JSON fields into a display string. */
export function coerceAnswerScalar(raw) {
  if (raw == null) return '';
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  if (typeof raw === 'boolean') return raw ? 'true' : 'false';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') {
    const inner =
      raw.text ??
      raw.value ??
      raw.label ??
      raw.answer ??
      raw.response ??
      raw.name ??
      raw.content;
    if (inner != null && typeof inner !== 'object') return String(inner);
  }
  return '';
}

/** Template `question_id` only (used for POST and matching FILLED / embedded answers). */
export function resolveQuestionIdForStep(question) {
  const t = Number(question?.question_id ?? question?.questionId);
  return Number.isFinite(t) ? t : null;
}

function isQuestionnaireSectionDictionary(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  return Object.values(obj).some(
    (v) => Array.isArray(v) && (v.length === 0 || (v[0] != null && typeof v[0] === 'object'))
  );
}

/** GET medical_questionnaire/summary: `sections` is `{ [title]: { status, questions } }`. */
function isBackendSummarySectionsMap(sections) {
  if (!sections || typeof sections !== 'object' || Array.isArray(sections)) return false;
  return Object.values(sections).some(
    (block) =>
      block &&
      typeof block === 'object' &&
      Array.isArray(block.questions) &&
      (block.questions.length === 0 ||
        (block.questions[0] != null && typeof block.questions[0] === 'object'))
  );
}

function questionnaireDataMapFromSummarySections(sections) {
  const out = {};
  for (const [title, block] of Object.entries(sections || {})) {
    if (!block || typeof block !== 'object') continue;
    const questions = block.questions;
    if (Array.isArray(questions)) out[title] = questions;
  }
  return out;
}

/** Unwrap summary GET envelope and read section completion stats from the backend. */
export function extractMedicalQuestionnaireSummaryStats(envelope) {
  let inner = envelope?.data !== undefined ? envelope.data : envelope;
  for (let i = 0; i < 5; i++) {
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    const hasStats =
      inner.total_sections != null ||
      inner.totalSections != null ||
      inner.completed_sections_percentage != null;
    if (hasStats) break;
    if (inner.data !== undefined) {
      inner = inner.data;
      continue;
    }
    break;
  }
  if (!inner || typeof inner !== 'object') {
    return {
      totalSections: 0,
      totalCompletedSections: 0,
      totalIncompleteSections: 0,
      completedSectionsPercentage: 0,
      overallStatus: '',
    };
  }
  const totalSections = Math.max(
    0,
    Number(inner.total_sections ?? inner.totalSections) || 0
  );
  const totalCompletedSections = Math.max(
    0,
    Number(inner.total_completed_sections ?? inner.totalCompletedSections) || 0
  );
  const totalIncompleteSections = Math.max(
    0,
    Number(inner.total_incomplete_sections ?? inner.totalIncompleteSections) ||
      (totalSections > 0 ? totalSections - totalCompletedSections : 0)
  );
  const completedSectionsPercentage = Math.min(
    100,
    Math.max(
      0,
      Number(
        inner.completed_sections_percentage ?? inner.completedSectionsPercentage
      ) || 0
    )
  );
  const overallStatus = String(inner.overall_status ?? inner.overallStatus ?? '').trim();
  return {
    totalSections,
    totalCompletedSections,
    totalIncompleteSections,
    completedSectionsPercentage,
    overallStatus,
  };
}

/**
 * Normalize GET questionnaire JSON into `{ [sectionTitle]: Question[] }`
 * whether the API uses `data: { "Section": [...] }`, `data: { sections: [...] } }`, or `data: [...]`.
 */
export function getQuestionnaireDataMap(payload) {
  if (!payload || typeof payload !== 'object') return {};
  let inner = payload.data !== undefined ? payload.data : payload;
  // Peel `{ data: { success, message, data: { "Section": [...] } } } }` so we never treat the
  // middle envelope as a section map (its `data` value is an object, not an array — filtered out).
  for (let i = 0; i < 5; i++) {
    if (!inner || typeof inner !== 'object') break;
    const nested = inner.data;
    if (
      nested != null &&
      typeof nested === 'object' &&
      !Array.isArray(nested) &&
      isQuestionnaireSectionDictionary(nested)
    ) {
      inner = nested;
    } else {
      break;
    }
  }

  if (Array.isArray(inner)) {
    const out = {};
    inner.forEach((section, idx) => {
      if (!section || typeof section !== 'object') return;
      const title =
        String(section.title || section.name || section.section_name || section.label || '').trim() ||
        `Section ${idx + 1}`;
      const questions =
        section.questions ||
        section.items ||
        section.question_set ||
        (Array.isArray(section.data) ? section.data : null);
      if (Array.isArray(questions)) out[title] = questions;
    });
    return out;
  }

  if (inner && typeof inner === 'object' && inner.sections && isBackendSummarySectionsMap(inner.sections)) {
    const out = questionnaireDataMapFromSummarySections(inner.sections);
    if (Object.keys(out).length) return out;
  }

  if (inner && typeof inner === 'object' && Array.isArray(inner.sections)) {
    const out = {};
    inner.sections.forEach((section, idx) => {
      if (!section || typeof section !== 'object') return;
      const title =
        String(section.title || section.name || section.section_name || '').trim() ||
        `Section ${idx + 1}`;
      const questions = section.questions || section.items || section.question_set || [];
      if (Array.isArray(questions)) out[title] = questions;
    });
    if (Object.keys(out).length) return out;
  }

  if (inner && typeof inner === 'object' && Array.isArray(inner.questions) && !inner.sections) {
    return { Questions: inner.questions };
  }

  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const entries = Object.entries(inner).filter(
      ([, v]) => Array.isArray(v) && (v.length === 0 || (v[0] != null && typeof v[0] === 'object'))
    );
    if (entries.length) return Object.fromEntries(entries);
  }

  return {};
}

/** Same ordering as UI sections: section map iteration order, questions sorted by `order`. */
export function getFlatQuestionsInSchemaOrder(payload) {
  const data = getQuestionnaireDataMap(payload);
  return Object.entries(data).flatMap(([, questions]) =>
    [...(questions || [])].sort((a, b) => (a.order || 0) - (b.order || 0))
  );
}

function firstDefined(source, keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
}

function toArray(value) {
  if (Array.isArray(value)) return value.filter((item) => item !== '');
  if (typeof value === 'string') {
    const parts = value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    return parts.length ? parts : [];
  }
  return [];
}

export function normalizeMedicalQuestionnairePrefill(raw) {
  const source =
    raw?.answers ||
    raw?.questionnaire ||
    raw?.filled_questionnaire ||
    raw?.data ||
    raw ||
    {};

  const prefill = {};
  const map = {
    heightCm: ['heightCm', 'height_cm'],
    weightKg: ['weightKg', 'weight_kg'],
    mainReason: ['mainReason', 'main_reason'],
    goalFocus: ['goalFocus', 'goal_focus'],
    symptomClusters: ['symptomClusters', 'symptom_clusters'],
    energyDipTiming: ['energyDipTiming', 'energy_dip_timing'],
    stressLevelCurrent: ['stressLevelCurrent', 'stress_level_current'],
    sleepDuration: ['sleepDuration', 'sleep_duration'],
    sleepIssue: ['sleepIssue', 'sleep_issue'],
    strengthTrainingFreq: ['strengthTrainingFreq', 'strength_training_freq'],
    cardioFreq: ['cardioFreq', 'cardio_freq'],
    dietType: ['dietType', 'diet_type'],
    alcoholSmokingStatus: ['alcoholSmokingStatus', 'alcohol_smoking_status'],
    supplementStack: ['supplementStack', 'supplement_stack'],
    supplementSpend: ['supplementSpend', 'supplement_spend'],
    medicationUse: ['medicationUse', 'medication_use'],
    medicationUseDetails: ['medicationUseDetails', 'medication_use_details'],
    medicalHistory: ['medicalHistory', 'medical_history'],
    allergyIntolerance: ['allergyIntolerance', 'allergy_intolerance'],
    allergyIntoleranceDetails: [
      'allergyIntoleranceDetails',
      'allergy_intolerance_details',
    ],
    fhxCvdEarly: ['fhxCvdEarly', 'fhx_cvd_early'],
    fhxDm2: ['fhxDm2', 'fhx_dm2'],
    fhxThyroid: ['fhxThyroid', 'fhx_thyroid'],
    recentInfection: ['recentInfection', 'recent_infection'],
    recentHeavyExercise: ['recentHeavyExercise', 'recent_heavy_exercise'],
    recentAlcohol: ['recentAlcohol', 'recent_alcohol'],
    additionalNotes: ['additionalNotes', 'additional_notes'],
  };

  for (const [targetKey, sourceKeys] of Object.entries(map)) {
    const value = firstDefined(source, sourceKeys);
    if (value === undefined) continue;
    if (
      targetKey === 'goalFocus' ||
      targetKey === 'symptomClusters' ||
      targetKey === 'supplementStack' ||
      targetKey === 'medicalHistory'
    ) {
      const arr = toArray(value);
      if (arr.length) prefill[targetKey] = arr;
      continue;
    }
    prefill[targetKey] = value;
  }

  return prefill;
}

function countDistinctFilledQuestionRows(rows) {
  const ids = new Set();
  for (const row of rows || []) {
    const id = Number(row?.question_id);
    if (!Number.isFinite(id)) continue;
    const hasAnswer =
      String(row?.answer ?? '').trim() !== '' ||
      (row?.options_answer_id != null &&
        (Array.isArray(row.options_answer_id)
          ? row.options_answer_id.length > 0
          : true));
    if (hasAnswer) ids.add(id);
  }
  return ids.size;
}

/** 1-based index of the next question to show (filled + 1, capped at total). */
export function resolveNextQuestionOneBased(filledCount, totalSteps) {
  const filled = Math.max(0, Number(filledCount) || 0);
  const total = Math.max(0, Number(totalSteps) || 0);
  if (total <= 0) return Math.max(1, filled + 1);
  if (filled >= total) return total;
  return filled + 1;
}

/**
 * Prefill answers plus how many questions the API reports as filled (for resume navigation).
 */
export async function fetchMedicalQuestionnaireResumeState(options = {}) {
  const { data: envelope } = await api.get(ENDPOINTS.MEDICAL_QUESTIONNAIRE.FILLED, {
    ...(options?.signal ? { signal: options.signal } : {}),
  });
  const payload = envelope?.data || envelope || {};
  const prefillAnswers = normalizeMedicalQuestionnairePrefill(payload);
  const qFields = extractLoginQuestionnaireFieldsFromEnvelope(envelope || {});
  persistLoginQuestionnaireFields(qFields);
  let numberOfQuestionsFilled = qFields.number_of_questions_filled;
  if (
    numberOfQuestionsFilled == null ||
    !Number.isFinite(Number(numberOfQuestionsFilled))
  ) {
    const rows = parseFilledMedicalQuestionnaireListFromEnvelope(envelope);
    numberOfQuestionsFilled = countDistinctFilledQuestionRows(rows);
  } else {
    numberOfQuestionsFilled = Number(numberOfQuestionsFilled);
  }
  return {
    prefillAnswers,
    numberOfQuestionsFilled: Math.max(0, numberOfQuestionsFilled),
    questionnaireFields: qFields,
  };
}

export async function fetchFilledMedicalQuestionnaire(options = {}) {
  const { prefillAnswers } = await fetchMedicalQuestionnaireResumeState(options);
  return prefillAnswers;
}

/**
 * Raw rows from GET filled questionnaire (same shape as POST items when API returns a list).
 */
export function parseFilledMedicalQuestionnaireListFromEnvelope(envelope) {
  let inner =
    envelope && typeof envelope === 'object' && envelope.data !== undefined
      ? envelope.data
      : envelope;
  // Some backends wrap FILLED payload as `{ success, message, data: ... }` (sometimes repeatedly).
  for (let i = 0; i < 5; i++) {
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    if (inner.data === undefined) break;
    inner = inner.data;
  }
  let list = [];
  if (Array.isArray(inner)) {
    list = inner;
  } else if (inner && typeof inner === 'object') {
    if (Array.isArray(inner.answers)) list = inner.answers;
    else if (Array.isArray(inner.results)) list = inner.results;
    else if (Array.isArray(inner.filled_answers)) list = inner.filled_answers;
    else if (Array.isArray(inner.items)) list = inner.items;
    else if (Array.isArray(inner.filled_medical_questionnaire))
      list = inner.filled_medical_questionnaire;
    else if (Array.isArray(inner.filled_questionnaire)) list = inner.filled_questionnaire;
    else if (Array.isArray(inner.questionnaire)) list = inner.questionnaire;
    else if (Array.isArray(inner.user_answers)) list = inner.user_answers;
    else {
      // Also support section maps like `{ "Basis & Profiel": [ ... ], ... }`.
      const sectionMap = getQuestionnaireDataMap(inner);
      const flattened = Object.values(sectionMap).flatMap((questions) =>
        Array.isArray(questions) ? questions : []
      );
      if (flattened.length) list = flattened;
    }
  }
  const out = [];
  for (const row of list) {
    if (!row || typeof row !== 'object') continue;
    const question_id = Number(row.question_id ?? row.questionId);
    if (!Number.isFinite(question_id)) continue;
    const answerRaw =
      row.answer ??
      row.text_answer ??
      row.value ??
      row.user_answer ??
      row.response ??
      row.text ??
      '';
    out.push({
      question_id,
      answer: answerRaw,
      options_answer_id:
        row.options_answer_id ?? row.optionsAnswerId ?? row.selected_options ?? row.options,
    });
  }
  return out;
}

export async function fetchFilledMedicalQuestionnaireAnswerRows(options) {
  const { data: envelope } = await api.get(ENDPOINTS.MEDICAL_QUESTIONNAIRE.FILLED, {
    ...(options?.signal ? { signal: options.signal } : {}),
  });
  return parseFilledMedicalQuestionnaireListFromEnvelope(envelope);
}

function optionsAnswerPayloadHasContent(options_answer_id) {
  return (
    options_answer_id != null &&
    (Array.isArray(options_answer_id)
      ? options_answer_id.length > 0
      : typeof options_answer_id === 'number'
      ? Number.isFinite(options_answer_id)
      : typeof options_answer_id === 'string'
      ? options_answer_id.trim() !== ''
      : typeof options_answer_id === 'object'
      ? Object.keys(options_answer_id).length > 0
      : false)
  );
}

/** Build `{ answer, options_answer_id? }` from a question definition (GET questions payload). */
function answerPrefillRowFromQuestion(q) {
  if (!q || typeof q !== 'object') return { answer: '' };
  const options_answer_id =
    q.options_answer_id ?? q.optionsAnswerId ?? q.selected_options ?? undefined;
  const answerRaw =
    q.answer ??
    q.user_answer ??
    q.given_answer ??
    q.response ??
    q.text ??
    q.value;
  const coerced = coerceAnswerScalar(answerRaw);
  const hasOpts = optionsAnswerPayloadHasContent(options_answer_id);
  const hasAns = coerced.trim() !== '';
  if (!hasOpts && !hasAns) return { answer: '' };
  return {
    answer: hasAns ? coerced : '',
    ...(hasOpts ? { options_answer_id } : {}),
  };
}

function answerPrefillRowHasSignal(row) {
  return (
    optionsAnswerPayloadHasContent(row?.options_answer_id) ||
    String(coerceAnswerScalar(row?.answer)).trim() !== ''
  );
}

/**
 * When GET questions returns each definition with embedded `answer` / `options_answer_id`
 * (same envelope as the schema: `data` is a map of section title → question[]).
 */
export function extractAnswerRowsFromQuestionsPayload(payload) {
  const data = getQuestionnaireDataMap(payload);
  const rows = [];
  for (const questions of Object.values(data)) {
    if (!Array.isArray(questions)) continue;
    for (const q of questions) {
      if (!q || typeof q !== 'object') continue;
      const question_id = Number(q.question_id ?? q.questionId);
      if (!Number.isFinite(question_id)) continue;
      const partial = answerPrefillRowFromQuestion(q);
      if (!answerPrefillRowHasSignal(partial)) continue;
      rows.push({ question_id, ...partial });
    }
  }
  return rows;
}

function normalizeOptionsAnswerList(raw) {
  if (raw == null) return [];
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return [String(raw)];
  }
  if (Array.isArray(raw)) {
    return raw
      .map((item) =>
        item != null && typeof item === 'object'
          ? item.value ?? item.label ?? item.id ?? item.option ?? ''
          : item
      )
      .map((x) => String(x ?? '').trim())
      .filter(Boolean);
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    if (t.startsWith('[')) {
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) return normalizeOptionsAnswerList(parsed);
      } catch (_) {
        // fall through
      }
    }
    return t.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function applyAnswerRowToOut(step, row, out) {
  if (!step || !row) return;
  const answerStr = coerceAnswerScalar(row.answer);

  if (step.type === 'choiceList' && step.multi) {
    const opts = normalizeOptionsAnswerList(row.options_answer_id);
    const fallback = opts.length ? opts : normalizeOptionsAnswerList(row.answer);
    if (fallback.length) {
      out[step.key] = step.optionBased
        ? mapPrefillValuesToOptionIds(fallback, step)
        : fallback;
    }
    return;
  }

  if (step.type === 'yesNoWithTextarea') {
    const opts = normalizeOptionsAnswerList(row.options_answer_id);
    if (opts.length) {
      const ids = mapPrefillValuesToOptionIds(opts, step);
      if (ids.length) out[step.key] = ids[0];
      const detailText = answerStr.trim();
      if (step.detailKey && detailText && stepDetailInputVisible(step, ids[0])) {
        out[step.detailKey] = detailText;
      }
      return;
    }

    const raw = answerStr.trim();
    if (!raw) return;
    const parts = raw.split(/\n\n+/);
    const mainPart = (parts[0] || '').trim();
    const rest = parts.length > 1 ? parts.slice(1).join('\n\n').trim() : '';
    const mainId = mapPrefillValuesToOptionIds([mainPart], step)[0] || mainPart;
    out[step.key] = mainId;
    if (step.detailKey && rest) {
      out[step.detailKey] = rest;
    } else if (
      step.detailKey &&
      mainPart &&
      stepDetailInputVisible(step, mainId) &&
      !rest &&
      !raw.includes('\n\n')
    ) {
      const maybe = raw.replace(mainPart, '').replace(/^\s*[\n:]\s*/, '').trim();
      if (maybe) out[step.detailKey] = maybe;
    }
    return;
  }

  if (step.type === 'choiceList' && !step.multi) {
    const opts = normalizeOptionsAnswerList(row.options_answer_id);
    if (step.optionBased && opts.length) {
      const ids = mapPrefillValuesToOptionIds(opts, step);
      if (ids.length) out[step.key] = ids[0];
      return;
    }
    const s = answerStr.trim();
    if (s) {
      if (step.optionBased && step.choiceOptions?.length) {
        const ids = mapPrefillValuesToOptionIds([s], step);
        if (ids.length) out[step.key] = ids[0];
      } else {
        out[step.key] = s;
      }
    }
    return;
  }

  const scalar = answerStr.trim();
  if (scalar) out[step.key] = scalar;
}

/**
 * Map GET filled questionnaire rows onto in-app `answers` keys (`step.key`, `step.detailKey`).
 * Rows are joined by `question_id` only (not instance `id` / FK aliases).
 *
 * @param {Array<{ question_id: number, answer?: unknown, options_answer_id?: unknown }>} rows
 * @param {Array<object>} steps - flat steps from {@link buildSectionsFromApi} + indices
 * @returns {Record<string, unknown>}
 */
function rowPrefillScore(row) {
  let s = 0;
  if (String(coerceAnswerScalar(row?.answer)).trim()) s += 2;
  const o = row?.options_answer_id;
  if (o != null && (Array.isArray(o) ? o.length : String(o).trim())) s += 2;
  return s;
}

export function buildAnswersFromFilledRows(rows, steps) {
  const byQid = new Map();
  for (const row of rows || []) {
    const qid = Number(row?.question_id ?? row?.questionId);
    if (!Number.isFinite(qid)) continue;
    const prev = byQid.get(qid);
    if (!prev || rowPrefillScore(row) >= rowPrefillScore(prev)) {
      byQid.set(qid, row);
    }
  }
  const out = {};
  for (const step of steps || []) {
    const qid = step?.questionId;
    if (!Number.isFinite(qid)) continue;
    const row = byQid.get(qid);
    if (!row) continue;
    applyAnswerRowToOut(step, row, out);
  }
  return out;
}

/** Map embedded answers onto steps by index in the schema (same order as {@link getFlatQuestionsInSchemaOrder}). */
export function buildAnswersFromQuestionOrder(payload, steps) {
  const rawList = getFlatQuestionsInSchemaOrder(payload);
  const out = {};
  const n = Math.min(rawList.length, (steps || []).length);
  for (let i = 0; i < n; i++) {
    applyAnswerRowToOut(steps[i], answerPrefillRowFromQuestion(rawList[i]), out);
  }
  return out;
}

function isEmptyPrefillMergeValue(v) {
  if (v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return true;
  return String(v).trim() === '';
}

/**
 * Baseline from question order in the payload, then overlay values matched by `question_id`.
 */
export function mergePrefillAnswers(rows, payload, steps) {
  const byOrder = buildAnswersFromQuestionOrder(payload, steps);
  const byId = buildAnswersFromFilledRows(rows, steps);
  const out = { ...byOrder };
  for (const [k, v] of Object.entries(byId)) {
    if (!isEmptyPrefillMergeValue(v)) out[k] = v;
  }
  return out;
}

/**
 * Build the final POST `answers` list: **one object per question** (stable JSON array for DRF).
 * Optional unanswered fields use `''` / `[]` as appropriate.
 *
 * @param {Record<string, unknown>} answers - in-app state keyed by step `key` / `detailKey`
 * @param {Array<object>} steps - flat steps with `questionId`, `key`, `type`, …
 * @returns {Array<{ question_id: number, answer?: string, options_answer_id?: unknown }>}
 */
export function buildFilledMedicalQuestionnairePayloadForSubmit(answers, steps) {
  if (!answers || typeof answers !== 'object' || !Array.isArray(steps)) return [];

  const list = [];
  for (const step of steps) {
    const qid = step?.questionId;
    if (!Number.isFinite(qid)) continue;

    const main = answers[step.key];
    const detailRaw = step.detailKey ? answers[step.detailKey] : '';

    const sendOptionsAnswerId =
      step.type === 'choiceList' &&
      (step.usesOptionsAnswerId || step.optionBased || step.multi);

    if (sendOptionsAnswerId) {
      const ids = resolveOptionIdsForSubmit(main, step);
      const row = {
        question_id: qid,
        options_answer_id: ids.length ? ids : [],
      };
      if (!ids.length) row.answer = '';
      list.push(row);
      continue;
    }

    if (step.type === 'yesNoWithTextarea') {
      list.push(buildYesNoWithTextareaSaveRow(step, main, detailRaw));
      continue;
    }

    if (step.type === 'choiceList' && !step.multi && !step.optionBased) {
      list.push({ question_id: qid, answer: String(main ?? '').trim() });
      continue;
    }

    list.push({ question_id: qid, answer: String(main ?? '').trim() });
  }

  return list;
}

/**
 * Stable fingerprint of what would be POSTed for one step (same shape as
 * {@link buildFilledMedicalQuestionnairePayloadForSubmit}). Used to skip redundant saves
 * when the user revisits a step without editing.
 *
 * @param {Record<string, unknown>} answers
 * @param {object} step
 * @returns {string}
 */
export function stepPersistSnapshotFromAnswers(answers, step) {
  const list = buildFilledMedicalQuestionnairePayloadForSubmit(answers, step ? [step] : []);
  if (!list.length) return `__no_row__:${step?.key || ''}`;
  return JSON.stringify(list[0]);
}

/** Mark questionnaire completed in persisted login/session state (no HTTP save). */
export function persistMedicalQuestionnaireCompletedOnClient() {
  persistLoginQuestionnaireFields({ medical_questionnaire_completed: true });
}

function unwrapFilledQuestionnaireSectionMap(envelope) {
  if (!envelope || typeof envelope !== 'object') return {};
  let inner = envelope.data !== undefined ? envelope.data : envelope;
  for (let i = 0; i < 5; i++) {
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    if (inner.data === undefined) break;
    inner = inner.data;
  }
  return getQuestionnaireDataMap(inner);
}

/** Collect `validation` rules from GET filled questionnaire (grouped by section). */
export function buildValidationByQuestionIdFromFilledEnvelope(envelope) {
  const map = {};
  const sectionMap = unwrapFilledQuestionnaireSectionMap(envelope);
  for (const questions of Object.values(sectionMap)) {
    if (!Array.isArray(questions)) continue;
    for (const question of questions) {
      if (!question || typeof question !== 'object') continue;
      const questionId = Number(question.question_id ?? question.questionId);
      if (!Number.isFinite(questionId)) continue;
      const rules = question.validation;
      if (rules && typeof rules === 'object' && !Array.isArray(rules)) {
        map[questionId] = rules;
      }
    }
  }
  return map;
}

/** Collect `options` from GET filled questionnaire when they include real option ids. */
export function buildOptionsByQuestionIdFromFilledEnvelope(envelope) {
  const map = {};
  const sectionMap = unwrapFilledQuestionnaireSectionMap(envelope);
  for (const questions of Object.values(sectionMap)) {
    if (!Array.isArray(questions)) continue;
    for (const question of questions) {
      if (!question || typeof question !== 'object') continue;
      const questionId = Number(question.question_id ?? question.questionId);
      if (!Number.isFinite(questionId)) continue;
      const opts =
        question.options ??
        question.question_options ??
        question.answer_options;
      if (!Array.isArray(opts) || !opts.length) continue;
      if (rawOptionsHaveAuthoritativeIds(opts)) {
        map[questionId] = opts;
      }
    }
  }
  return map;
}

/**
 * @param {unknown} error - axios error from save POST
 * @param {number|null|undefined} questionId
 * @returns {string|null}
 */
export function parseMedicalQuestionnaireSaveValidationError(error, questionId) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const errors = data?.errors;
  if (status !== 400 || !Array.isArray(errors) || !errors.length) return null;

  const qid = Number(questionId);
  if (Number.isFinite(qid)) {
    const match = errors.find((e) => Number(e?.question_id) === qid);
    if (match?.detail) return String(match.detail);
  }
  if (errors.length === 1 && errors[0]?.detail) {
    return String(errors[0].detail);
  }
  return null;
}

/** Bulk questionnaire save can exceed the default axios 20s timeout; override via env if needed. */
const MEDICAL_QUESTIONNAIRE_SAVE_TIMEOUT_MS = (() => {
  const raw = process.env.REACT_APP_MEDICAL_QUESTIONNAIRE_SAVE_TIMEOUT_MS;
  if (raw == null || String(raw).trim() === '') return 120000;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 10000 ? n : 120000;
})();

/**
 * Many Django views use `FilledMedicalQuestionnaireSerializer(data=request.data, many=True)`,
 * which requires the **HTTP JSON body to be a top-level array** `[{...}, {...}]`. Sending
 * `{ "answers": [...] }` makes `request.data` a dict, so `isinstance(request.data, list)` fails
 * with errors like "Payload must be a list of answers."
 *
 * Set `REACT_APP_SAVE_MEDICAL_QUESTIONNAIRE_JSON_ENVELOPE=answers` if your API expects
 * `{ "answers": [ ... ] }` instead.
 *
 * Timeout defaults to 120s (`REACT_APP_MEDICAL_QUESTIONNAIRE_SAVE_TIMEOUT_MS`, min 10000).
 *
 * @param {Array<{ question_id: number, answer?: string, options_answer_id?: unknown }>} answerList
 * @returns {Promise<unknown>}
 */
async function postMedicalQuestionnaireAnswerRows(answerList) {
  if (!Array.isArray(answerList) || answerList.length === 0) {
    throw new Error('No answers to submit');
  }
  const listCopy = answerList.map((row) => ({ ...row }));
  const useAnswersEnvelope =
    String(process.env.REACT_APP_SAVE_MEDICAL_QUESTIONNAIRE_JSON_ENVELOPE || '')
      .trim()
      .toLowerCase() === 'answers';
  const body = useAnswersEnvelope ? { answers: listCopy } : listCopy;
  const { data: envelope } = await api.post(ENDPOINTS.MEDICAL_QUESTIONNAIRE.SAVE, body, {
    timeout: MEDICAL_QUESTIONNAIRE_SAVE_TIMEOUT_MS,
  });
  const payload = envelope?.data ?? envelope;
  const fromEnvelope = extractLoginQuestionnaireFieldsFromEnvelope(envelope || {});
  const fromPayload = extractLoginQuestionnaireFields(
    payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}
  );
  const inner =
    envelope?.data && typeof envelope.data === 'object' && !Array.isArray(envelope.data)
      ? envelope.data
      : payload && typeof payload === 'object' && !Array.isArray(payload)
        ? payload
        : {};
  const sectionProgress =
    fromEnvelope.section_progress ??
    fromPayload.section_progress ??
    normalizeQuestionnaireCompletionPercentage(inner.section_progress) ??
    normalizeQuestionnaireCompletionPercentage(inner.completed_sections_percentage);
  const sectionsCompleted =
    fromEnvelope.sections_completed ??
    fromPayload.sections_completed ??
    (() => {
      const raw =
        inner.section_completed ??
        inner.sections_completed ??
        inner.sectionCompleted ??
        inner.sectionsCompleted;
      if (raw == null || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
    })();
  const questionnaireFields = {
    medical_questionnaire_completed:
      fromEnvelope.medical_questionnaire_completed ??
      fromPayload.medical_questionnaire_completed,
    number_of_questions_filled:
      fromEnvelope.number_of_questions_filled ?? fromPayload.number_of_questions_filled,
    questionnaire_completion_percentage:
      fromEnvelope.questionnaire_completion_percentage ??
      fromPayload.questionnaire_completion_percentage,
    sections_completed: sectionsCompleted,
    section_progress: sectionProgress,
    total_sections: fromEnvelope.total_sections ?? fromPayload.total_sections,
  };
  persistLoginQuestionnaireFields(questionnaireFields);
  return {
    payload,
    questionnaireFields,
  };
}

/**
 * Persist the full questionnaire in one request (submit). Prefer {@link saveMedicalQuestionnaireStepAnswer}
 * for step-by-step saves from the UI.
 *
 * @param {Record<string, unknown>} answers
 * @param {Array<object>} steps
 * @returns {Promise<unknown>}
 */
export async function saveMedicalQuestionnaireAnswers(answers, steps) {
  const answerList = buildFilledMedicalQuestionnairePayloadForSubmit(answers, steps);
  const { payload, questionnaireFields } = await postMedicalQuestionnaireAnswerRows(answerList);
  persistLoginQuestionnaireFields({
    medical_questionnaire_completed: true,
    questionnaire_completion_percentage: 100,
    ...questionnaireFields,
  });
  return {
    payload,
    questionnaireFields: {
      ...questionnaireFields,
      medical_questionnaire_completed: true,
      questionnaire_completion_percentage: 100,
    },
  };
}

/**
 * Save one question row to the same endpoint as bulk (one-item array or `{ answers: [row] }`).
 * Use `markQuestionnaireComplete: true` only after the final step succeeds.
 *
 * @param {Record<string, unknown>} answers
 * @param {object} step - one flat step from the flow (must have `questionId` when persisted)
 * @param {{ markQuestionnaireComplete?: boolean }} [options]
 * @returns {Promise<unknown>}
 */
export async function saveMedicalQuestionnaireStepAnswer(answers, step, options = {}) {
  const answerList = buildFilledMedicalQuestionnairePayloadForSubmit(answers, step ? [step] : []);
  if (!Array.isArray(answerList) || answerList.length === 0) {
    throw new Error('No answer to save for this question');
  }
  const { payload, questionnaireFields } = await postMedicalQuestionnaireAnswerRows(answerList);
  if (options.markQuestionnaireComplete) {
    persistLoginQuestionnaireFields({
      medical_questionnaire_completed: true,
      questionnaire_completion_percentage: 100,
    });
    return {
      payload,
      questionnaireFields: {
        ...questionnaireFields,
        medical_questionnaire_completed: true,
        questionnaire_completion_percentage: 100,
      },
    };
  }
  return { payload, questionnaireFields };
}

/** Unwrap questionnaire GET bodies into a section map (questions list or summary `sections`). */
export function normalizeMergedQuestionnairePayload(envelope) {
  let inner = envelope?.data !== undefined ? envelope.data : envelope;
  for (let i = 0; i < 5; i++) {
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    const nested = inner.data;
    if (
      nested != null &&
      typeof nested === 'object' &&
      !Array.isArray(nested) &&
      getQuestionnaireDataMap(nested) &&
      Object.keys(getQuestionnaireDataMap(nested)).length
    ) {
      inner = nested;
      continue;
    }
    break;
  }
  const map = getQuestionnaireDataMap(inner);
  return Object.keys(map).length ? map : inner;
}

/** Build sections/steps/answers from a merged questionnaire payload (embedded answers per question). */
export function buildQuestionnaireContextFromPayload(json) {
  const sections = buildSectionsFromApi(json);
  const steps = flatStepsFromSections(sections);
  const filledRows = extractAnswerRowsFromQuestionsPayload(json);
  const answers = mergePrefillAnswers(filledRows, json, steps);
  return { sections, steps, answers };
}

/**
 * Summary page (read-only): one GET to medical_questionnaire/summary.
 * Edit flow loads its own data after navigating to /medical-questionnaire/questions.
 */
export async function loadMedicalQuestionnaireSummaryContext({ signal, self } = {}) {
  const requestOpts = signal ? { signal } : {};
  if (self) {
    requestOpts.params = { self: 'true' };
  }
  const { data: envelope } = await api.get(ENDPOINTS.MEDICAL_QUESTIONNAIRE.SUMMARY, requestOpts);
  const sections = buildSummaryUiSectionsFromSummaryEnvelope(envelope);
  if (!sections.length) {
    throw new Error('No questionnaire data');
  }
  return {
    sections,
    stats: extractMedicalQuestionnaireSummaryStats(envelope),
  };
}

/**
 * Load questions + filled answers (two GETs). Used by questionnaire flow and submit reload.
 */
export async function loadMedicalQuestionnaireSubmitContext({ signal } = {}) {
  const requestOpts = signal ? { signal } : {};
  const [questionsRes, filledRes] = await Promise.all([
    api.get(ENDPOINTS.MEDICAL_QUESTIONNAIRE.QUESTIONS, requestOpts),
    api.get(ENDPOINTS.MEDICAL_QUESTIONNAIRE.FILLED, requestOpts),
  ]);
  const filledEnvelope = filledRes?.data;
  const json = mergeOptionsIntoQuestionnairePayload(
    mergeValidationIntoQuestionnairePayload(
      questionsRes?.data,
      buildValidationByQuestionIdFromFilledEnvelope(filledEnvelope)
    ),
    buildOptionsByQuestionIdFromFilledEnvelope(filledEnvelope)
  );
  const { sections, steps } = buildQuestionnaireContextFromPayload(json);
  const filledRows = parseFilledMedicalQuestionnaireListFromEnvelope(filledEnvelope);
  const answers = mergePrefillAnswers(
    [...filledRows, ...extractAnswerRowsFromQuestionsPayload(json)],
    json,
    steps
  );
  return { sections, steps, answers };
}

/** Final submit from summary using saved answers (re-fetches FILLED + schema). */
export async function submitMedicalQuestionnaireFromSavedAnswers(options = {}) {
  const { steps, answers } = await loadMedicalQuestionnaireSubmitContext(options);
  return saveMedicalQuestionnaireAnswers(answers, steps);
}

/** Section count only (for "X of Y sections" label); completion % comes from BE profile field. */
export async function fetchMedicalQuestionnaireTotalSections({ signal } = {}) {
  const { data: json } = await api.get(ENDPOINTS.MEDICAL_QUESTIONNAIRE.QUESTIONS, {
    ...(signal ? { signal } : {}),
  });
  const sections = buildSectionsFromApi(json);
  return sections.length;
}

